import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { mockArticles, mockSections } from '@/mocks/articles';
import { mockAds } from '@/mocks/ads';

const statusStyles: Record<string, string> = {
  rascunho: 'border-[#999999] text-[#666666]',
  revisão: 'border-[#B8860B] text-[#B8860B]',
  aprovado: 'border-[#1a7a1a] text-[#1a7a1a]',
  publicado: 'border-[#111111] text-[#111111]',
};

const priorityStyles: Record<string, string> = {
  alta: 'text-[#C41230]',
  média: 'text-[#B8860B]',
  baixa: 'text-[#999999]',
};

const sectionColors: Record<string, string> = {
  Política: 'text-[#C41230]',
  Esportes: 'text-[#1a7a1a]',
  Cultura: 'text-[#5B2D8E]',
  Economia: 'text-[#B8860B]',
  Ciência: 'text-[#0066AA]',
};

type Tab = 'artigos' | 'anuncios';

export default function ContentPage() {
  const [tab, setTab] = useState<Tab>('artigos');
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showNewModal, setShowNewModal] = useState(false);

  const filtered = mockArticles.filter((a) => {
    const matchSearch =
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.author.toLowerCase().includes(search.toLowerCase());
    const matchSection = !sectionFilter || a.section === sectionFilter;
    const matchStatus = !statusFilter || a.status === statusFilter;
    return matchSearch && matchSection && matchStatus;
  });

  return (
    <AppLayout>
      <div className="p-6">

        {/* Tab Bar + Action */}
        <div className="flex items-center justify-between mb-5 border-b-2 border-[#111111] pb-3">
          <div className="flex items-center gap-0">
            {(['artigos', 'anuncios'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-5 py-1.5 text-xs font-bold uppercase tracking-wider font-sans cursor-pointer whitespace-nowrap transition-colors border-b-2 -mb-[14px] ${
                  tab === t
                    ? 'border-[#C41230] text-[#C41230]'
                    : 'border-transparent text-[#666666] hover:text-[#111111]'
                }`}
              >
                {t === 'artigos' ? 'Artigos' : 'Anúncios'}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#111111] text-white text-xs font-bold font-sans uppercase tracking-wide hover:bg-[#333333] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-sm"></i>
            {tab === 'artigos' ? 'Novo Artigo' : 'Novo Anúncio'}
          </button>
        </div>

        {tab === 'artigos' && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-[#E0E0E0]">
              <div className="relative flex-1 min-w-48">
                <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-[#999999] text-xs"></i>
                <input
                  type="text"
                  placeholder="Buscar artigos, autores..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-7 pr-3 py-1.5 text-xs bg-white border border-[#E0E0E0] focus:outline-none focus:border-[#111111] font-sans transition-colors"
                />
              </div>
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                className="px-3 py-1.5 text-xs bg-white border border-[#E0E0E0] focus:outline-none focus:border-[#111111] cursor-pointer font-sans"
              >
                <option value="">Todas as seções</option>
                {mockSections.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 text-xs bg-white border border-[#E0E0E0] focus:outline-none focus:border-[#111111] cursor-pointer font-sans"
              >
                <option value="">Todos os status</option>
                <option value="rascunho">Rascunho</option>
                <option value="revisão">Em Revisão</option>
                <option value="aprovado">Aprovado</option>
                <option value="publicado">Publicado</option>
              </select>
              <div className="flex items-center gap-0 border border-[#E0E0E0]">
                <button
                  onClick={() => setViewMode('list')}
                  className={`w-7 h-7 flex items-center justify-center cursor-pointer transition-colors ${viewMode === 'list' ? 'bg-[#111111] text-white' : 'bg-white text-[#666666] hover:bg-[#F0F0F0]'}`}
                >
                  <i className="ri-list-check text-xs"></i>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`w-7 h-7 flex items-center justify-center cursor-pointer transition-colors ${viewMode === 'grid' ? 'bg-[#111111] text-white' : 'bg-white text-[#666666] hover:bg-[#F0F0F0]'}`}
                >
                  <i className="ri-grid-line text-xs"></i>
                </button>
              </div>
              <span className="text-[11px] text-[#999999] font-sans">{filtered.length} artigos encontrados</span>
            </div>

            {/* List View */}
            {viewMode === 'list' ? (
              <div className="bg-white border border-[#E0E0E0]">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-[#111111]">
                      <th className="text-left text-[10px] font-bold text-[#111111] uppercase tracking-wider px-5 py-2.5 font-sans">Artigo</th>
                      <th className="text-left text-[10px] font-bold text-[#111111] uppercase tracking-wider px-3 py-2.5 font-sans hidden md:table-cell">Autor</th>
                      <th className="text-left text-[10px] font-bold text-[#111111] uppercase tracking-wider px-3 py-2.5 font-sans hidden lg:table-cell">Seção</th>
                      <th className="text-left text-[10px] font-bold text-[#111111] uppercase tracking-wider px-3 py-2.5 font-sans hidden lg:table-cell">Prioridade</th>
                      <th className="text-left text-[10px] font-bold text-[#111111] uppercase tracking-wider px-3 py-2.5 font-sans">Status</th>
                      <th className="text-left text-[10px] font-bold text-[#111111] uppercase tracking-wider px-3 py-2.5 font-sans hidden md:table-cell">Palavras</th>
                      <th className="px-3 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a, idx) => (
                      <tr
                        key={a.id}
                        className={`border-b border-[#F0F0F0] hover:bg-[#F8F8F8] transition-colors cursor-pointer ${idx % 2 === 0 ? '' : 'bg-[#FAFAFA]'}`}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 overflow-hidden border border-[#E0E0E0] flex-shrink-0">
                              <img src={a.image} alt="" className="w-full h-full object-cover object-top" />
                            </div>
                            <p className="text-xs font-bold text-[#111111] font-serif line-clamp-1 max-w-xs">{a.title}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3 hidden md:table-cell">
                          <span className="text-xs text-[#444444] font-sans">{a.author}</span>
                        </td>
                        <td className="px-3 py-3 hidden lg:table-cell">
                          <span className={`text-[10px] font-bold font-sans ${sectionColors[a.section] || 'text-[#666666]'}`}>{a.section}</span>
                        </td>
                        <td className="px-3 py-3 hidden lg:table-cell">
                          <span className={`text-[10px] font-bold font-sans uppercase ${priorityStyles[a.priority]}`}>{a.priority}</span>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`text-[9px] font-bold px-2 py-0.5 border font-sans uppercase tracking-wide ${statusStyles[a.status]}`}>
                            {a.status}
                          </span>
                        </td>
                        <td className="px-3 py-3 hidden md:table-cell">
                          <span className="text-xs text-[#999999] font-sans">{a.wordCount}</span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <button className="w-6 h-6 flex items-center justify-center hover:bg-[#F0F0F0] cursor-pointer transition-colors">
                              <i className="ri-edit-line text-[#666666] text-xs"></i>
                            </button>
                            <button className="w-6 h-6 flex items-center justify-center hover:bg-[#F0F0F0] cursor-pointer transition-colors">
                              <i className="ri-robot-line text-[#111111] text-xs"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Grid View */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E0E0E0]">
                {filtered.map((a, idx) => (
                  <div
                    key={a.id}
                    className={`bg-white hover:bg-[#F8F8F8] transition-colors cursor-pointer border-b border-r border-[#E0E0E0] ${idx % 3 === 2 ? 'border-r-0' : ''}`}
                  >
                    <div className="h-36 overflow-hidden border-b border-[#E0E0E0]">
                      <img src={a.image} alt="" className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-[9px] font-bold font-sans uppercase tracking-wide ${sectionColors[a.section] || 'text-[#666666]'}`}>{a.section}</span>
                        <span className="text-[#C8C8C8]">·</span>
                        <span className={`text-[9px] font-bold border px-1.5 py-0.5 font-sans uppercase tracking-wide ${statusStyles[a.status]}`}>{a.status}</span>
                      </div>
                      <p className="text-sm font-bold text-[#111111] font-serif line-clamp-2 mb-2 leading-tight">{a.title}</p>
                      <p className="text-[11px] text-[#666666] font-sans">{a.author} · {a.wordCount} palavras</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Ads Tab */}
        {tab === 'anuncios' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E0E0E0]">
            {mockAds.map((ad, idx) => (
              <div
                key={ad.id}
                className={`bg-white hover:bg-[#F8F8F8] transition-colors cursor-pointer border-b border-r border-[#E0E0E0] ${idx % 3 === 2 ? 'border-r-0' : ''}`}
              >
                <div className="h-36 overflow-hidden border-b border-[#E0E0E0]">
                  <img src={ad.image} alt="" className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[9px] font-bold border px-1.5 py-0.5 font-sans uppercase tracking-wide ${ad.status === 'ativo' ? 'border-[#1a7a1a] text-[#1a7a1a]' : 'border-[#B8860B] text-[#B8860B]'}`}>
                      {ad.status}
                    </span>
                    <span className="text-[10px] text-[#999999] font-sans">{ad.size.replace(/_/g, ' ')}</span>
                  </div>
                  <p className="text-sm font-bold text-[#111111] font-serif line-clamp-1 mb-1">{ad.title}</p>
                  <p className="text-[11px] text-[#666666] font-sans">{ad.client}</p>
                  <p className="text-[10px] text-[#999999] font-sans mt-1">{ad.startDate} → {ad.endDate}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Article Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#E0E0E0]">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#111111]">
              <div>
                <p className="section-label mb-0.5">CMS</p>
                <h2 className="text-lg font-bold text-[#111111] font-serif leading-none">Novo Artigo</h2>
              </div>
              <button
                onClick={() => setShowNewModal(false)}
                className="w-7 h-7 flex items-center justify-center hover:bg-[#F0F0F0] cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-[#444444] text-sm"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-[#111111] uppercase tracking-wider mb-1.5 font-sans">Título</label>
                <input
                  type="text"
                  placeholder="Digite o título do artigo..."
                  className="w-full px-3 py-2 text-sm border border-[#E0E0E0] focus:outline-none focus:border-[#111111] font-serif transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-[#111111] uppercase tracking-wider mb-1.5 font-sans">Autor</label>
                  <input
                    type="text"
                    placeholder="Nome do autor"
                    className="w-full px-3 py-2 text-sm border border-[#E0E0E0] focus:outline-none focus:border-[#111111] font-sans transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#111111] uppercase tracking-wider mb-1.5 font-sans">Seção</label>
                  <select className="w-full px-3 py-2 text-sm border border-[#E0E0E0] focus:outline-none focus:border-[#111111] cursor-pointer font-sans">
                    {mockSections.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-[#111111] uppercase tracking-wider mb-1.5 font-sans">Conteúdo</label>
                <textarea
                  rows={6}
                  placeholder="Escreva o conteúdo do artigo..."
                  className="w-full px-3 py-2 text-sm border border-[#E0E0E0] focus:outline-none focus:border-[#111111] resize-none font-source-serif transition-colors"
                ></textarea>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-[#111111] uppercase tracking-wider mb-1.5 font-sans">Prioridade</label>
                  <select className="w-full px-3 py-2 text-sm border border-[#E0E0E0] focus:outline-none focus:border-[#111111] cursor-pointer font-sans">
                    <option value="alta">Alta</option>
                    <option value="média">Média</option>
                    <option value="baixa">Baixa</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#111111] uppercase tracking-wider mb-1.5 font-sans">Tags</label>
                  <input
                    type="text"
                    placeholder="política, cidade, 2026"
                    className="w-full px-3 py-2 text-sm border border-[#E0E0E0] focus:outline-none focus:border-[#111111] font-sans transition-colors"
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-[#E0E0E0] pt-4 flex items-center gap-3">
                <button
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 py-2 text-xs font-bold text-[#444444] border border-[#E0E0E0] hover:bg-[#F0F0F0] cursor-pointer font-sans uppercase tracking-wide whitespace-nowrap transition-colors"
                >
                  Cancelar
                </button>
                <button className="flex-1 py-2 text-xs font-bold text-white bg-[#111111] hover:bg-[#333333] cursor-pointer font-sans uppercase tracking-wide whitespace-nowrap transition-colors">
                  Salvar Artigo
                </button>
                <button className="flex-1 py-2 text-xs font-bold text-white bg-[#C41230] hover:bg-[#a00e26] cursor-pointer font-sans uppercase tracking-wide whitespace-nowrap transition-colors">
                  <i className="ri-robot-line mr-1"></i>Processar com IA
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
